import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { createToggleRow } from '@learnforge/demo-kit';
import {
  solveInequality,
  type Interval,
  type InequalityStep,
  isNegativeInfinity,
  isPositiveInfinity,
  parse,
  format,
  Fraction,
  describeInequalityStep,
  type DescribeI18n,
} from '../algebra';
import type { Comparator } from '../algebra/inequality';
import type { AlgebraContext, AlgebraKey } from '../types';
import { createSvgEl } from '@learnforge/demo-render';
import { createSignChart, type SignChartOptions } from '../render/sign-chart';
import { mountActionBar } from '../action-bar';
import { INEQUALITY_PRESETS } from '../data/inequality-presets';

// ============================================================================
// Pure Helper Functions (exported and tested)
// ============================================================================

export interface ShadedSegment {
  x1: number;
  x2: number;
}

export interface EndpointMarker {
  x: number;
  kind: 'open' | 'closed';
}

/**
 * Map Interval[] to pixel shaded segments on the number line.
 * Each interval with finite endpoints is clipped to viewRange.
 * For ±∞ endpoints, clamp to viewRange boundaries and emit no marker.
 */
export function intervalsToShadedSegments(
  intervals: readonly Interval[],
  viewRange: { min: number; max: number },
  pixelWidth: number,
): ShadedSegment[] {
  const segments: ShadedSegment[] = [];

  for (const interval of intervals) {
    // Get numeric values for start and end, clamping infinities
    let startVal = viewRange.min;
    if (!isNegativeInfinity(interval.start)) {
      if (interval.start.kind === 'num') {
        startVal = interval.start.value.toNumber();
      }
    }
    startVal = Math.max(startVal, viewRange.min);

    let endVal = viewRange.max;
    if (!isPositiveInfinity(interval.end)) {
      if (interval.end.kind === 'num') {
        endVal = interval.end.value.toNumber();
      }
    }
    endVal = Math.min(endVal, viewRange.max);

    // Convert to pixel coordinates
    const range = viewRange.max - viewRange.min;
    const x1 = ((startVal - viewRange.min) / range) * pixelWidth;
    const x2 = ((endVal - viewRange.min) / range) * pixelWidth;

    if (x2 > x1) {
      segments.push({ x1, x2 });
    }
  }

  return segments;
}

/**
 * Extract endpoint markers from intervals and asymptotes.
 * Finite interval endpoints emit markers with kind = open/closed.
 * Asymptotes and infinite endpoints emit no markers.
 */
export function intervalsAndAsymptotesToEndpointMarkers(
  intervals: readonly Interval[],
  asymptotes: readonly Fraction[],
  viewRange: { min: number; max: number },
  pixelWidth: number,
): EndpointMarker[] {
  const range = viewRange.max - viewRange.min;

  // Collect all endpoints
  const endpoints: Array<{ value: number; kind: 'open' | 'closed' }> = [];

  for (const interval of intervals) {
    // Start endpoint
    if (!isNegativeInfinity(interval.start) && interval.start.kind === 'num') {
      const val = interval.start.value.toNumber();
      if (val >= viewRange.min && val <= viewRange.max) {
        endpoints.push({
          value: val,
          kind: interval.openLeft ? 'open' : 'closed',
        });
      }
    }

    // End endpoint
    if (!isPositiveInfinity(interval.end) && interval.end.kind === 'num') {
      const val = interval.end.value.toNumber();
      if (val >= viewRange.min && val <= viewRange.max) {
        endpoints.push({
          value: val,
          kind: interval.openRight ? 'open' : 'closed',
        });
      }
    }
  }

  // Add asymptote markers (always 'open')
  for (const asympFrac of asymptotes) {
    const val = asympFrac.toNumber();
    if (val >= viewRange.min && val <= viewRange.max) {
      endpoints.push({
        value: val,
        kind: 'open',
      });
    }
  }

  // Convert to pixel coordinates and deduplicate by pixel position
  const markerMap = new Map<number, EndpointMarker>();
  for (const endpoint of endpoints) {
    const x = ((endpoint.value - viewRange.min) / range) * pixelWidth;
    const key = Math.round(x); // Group close pixels
    if (!markerMap.has(key)) {
      markerMap.set(key, { x, kind: endpoint.kind });
    }
  }

  return Array.from(markerMap.values()).sort((a, b) => a.x - b.x);
}

// ============================================================================
// SVG Rendering Helpers
// ============================================================================

/**
 * Format one solution interval as bracket notation, e.g. `(-∞, 6)` or `[2, 5]`.
 * `format()` already renders the ±∞ sentinel Exprs as their literal glyphs.
 */
function formatInterval(interval: Interval): string {
  const left = interval.openLeft ? '(' : '[';
  const right = interval.openRight ? ')' : ']';
  const start = format(interval.start, { useUnicodeSuperscripts: true });
  const end = format(interval.end, { useUnicodeSuperscripts: true });
  return `${left}${start}, ${end}${right}`;
}

/**
 * The concrete, input-dependent content for a step — beyond its generic i18n
 * rule label — derived from the step's own payload (see InequalityStep in
 * src/algebra/inequality.ts). Returns '' when there's nothing useful to add
 * inline (e.g. the sign-chart step's payload is the chart itself, which is
 * already rendered visually below — repeating it as text would be redundant).
 */
function describeStepContent(step: InequalityStep, comparator: Comparator): string {
  switch (step.rule) {
    case 'move-to-one-side':
    case 'factor':
      return `${format(step.after, { useUnicodeSuperscripts: true })} ${comparator} 0`;
    case 'identify-critical-points':
    case 'identify-asymptotes':
      return step.points.map((p) => p.toString()).join(', ');
    case 'read-off-intervals':
      return step.intervals.length > 0
        ? step.intervals.map((iv) => formatInterval(iv)).join(' ∪ ')
        : '∅';
    case 'evaluate-sign-chart':
      return '';
  }
}

/**
 * Render the solver's step list as a numbered list top-left of the stage.
 * Each line is the generic i18n rule label plus the concrete content for
 * THIS problem (its actual expressions/points/intervals), so the list reads
 * as a real step-by-step solution rather than the same five rule names for
 * every preset. Caps at 5 visible lines; any remainder collapses into a
 * "+N more" line.
 */
function renderSteps(
  svg: SVGSVGElement,
  steps: readonly InequalityStep[],
  i18n: DescribeI18n,
  comparator: Comparator,
): void {
  const x = 50;
  const firstY = 24;
  const lineHeight = 18;
  const maxSteps = 5;

  const visible = steps.slice(0, maxSteps);
  visible.forEach((step, i) => {
    const stepText = createSvgEl('text', {
      x,
      y: firstY + i * lineHeight,
      class: 'alg-steps',
    });
    const label = describeInequalityStep(step, i18n);
    const content = describeStepContent(step, comparator);
    stepText.textContent = content ? `${i + 1}. ${label}: ${content}` : `${i + 1}. ${label}`;
    svg.appendChild(stepText);
  });

  if (steps.length > maxSteps) {
    const moreText = createSvgEl('text', {
      x,
      y: firstY + visible.length * lineHeight,
      class: 'alg-text-mute',
    });
    moreText.textContent = `+${steps.length - maxSteps} more`;
    svg.appendChild(moreText);
  }
}

function renderNumberLine(
  svg: SVGSVGElement,
  segments: ShadedSegment[],
  markers: EndpointMarker[],
  viewRange: { min: number; max: number },
  pixelWidth: number,
): void {
  const lineY = 150;
  const lineX1 = 50;
  const lineX2 = lineX1 + pixelWidth;
  const tickHeight = 8;
  const shadeHeight = 30;
  const shadeY = lineY - shadeHeight / 2;

  // Draw shaded regions
  for (const seg of segments) {
    const shadeRect = createSvgEl('rect', {
      x: lineX1 + seg.x1,
      y: shadeY,
      width: seg.x2 - seg.x1,
      height: shadeHeight,
      class: 'alg-shade',
    });
    svg.appendChild(shadeRect);
  }

  // Draw main axis line
  const line = createSvgEl('line', {
    x1: lineX1,
    y1: lineY,
    x2: lineX2,
    y2: lineY,
    class: 'alg-axis',
  });
  svg.appendChild(line);

  // Draw tick marks and labels
  const numTicks = 11;
  for (let i = 0; i < numTicks; i += 1) {
    const fraction = i / (numTicks - 1);
    const x = lineX1 + fraction * pixelWidth;
    const value = viewRange.min + fraction * (viewRange.max - viewRange.min);

    const tick = createSvgEl('line', {
      x1: x,
      y1: lineY - tickHeight / 2,
      x2: x,
      y2: lineY + tickHeight / 2,
      class: 'alg-tick',
    });
    svg.appendChild(tick);

    // Add label every other tick
    if (i % 2 === 0) {
      const label = createSvgEl('text', {
        x,
        y: lineY + tickHeight + 15,
        'text-anchor': 'middle',
        'font-size': 12,
        'font-family': 'sans-serif',
        class: 'alg-text-mute',
      });
      label.textContent = Math.round(value).toString();
      svg.appendChild(label);
    }
  }

  // Draw endpoint markers
  for (const marker of markers) {
    const x = lineX1 + marker.x;

    if (marker.kind === 'open') {
      // Open circle
      const circle = createSvgEl('circle', {
        cx: x,
        cy: lineY,
        r: 5,
        class: 'alg-endpoint-open',
      });
      svg.appendChild(circle);
    } else {
      // Closed dot
      const dot = createSvgEl('circle', {
        cx: x,
        cy: lineY,
        r: 4,
        class: 'alg-endpoint-closed',
      });
      svg.appendChild(dot);
    }
  }
}

// ============================================================================
// State
// ============================================================================

interface State {
  presetId: string;
  showSignChart: boolean;
  lhs: string;
  comparator: Comparator;
  rhs: string;
}

// ============================================================================
// Spec factory
// ============================================================================

const TARGET_VAR = 'x';
const VIEW_RANGE = { min: -10, max: 10 };
const LINE_PIXEL_WIDTH = 800;

function buildSpec(): CustomExperimentSpec<State, AlgebraKey, AlgebraContext> {
  return {
    labelKey: 'experiment.inequality-line.label',
    demoId: 'algebra',
    advancedKey: 'inequality-line',

    initialState(): State {
      const defaultPreset = INEQUALITY_PRESETS[0]!;
      return {
        presetId: defaultPreset.id,
        showSignChart: true,
        lhs: defaultPreset.lhs,
        comparator: defaultPreset.comparator,
        rhs: defaultPreset.rhs,
      };
    },

    applyPreset(state, presetId): State {
      const preset = INEQUALITY_PRESETS.find((p) => p.id === presetId);
      if (!preset) return state;
      return {
        ...state,
        presetId: preset.id,
        lhs: preset.lhs,
        comparator: preset.comparator,
        rhs: preset.rhs,
      };
    },

    createRender(ctx, host, controls) {
      // ── DOM: SVG stage ──
      const svg = createSvgEl('svg', {
        viewBox: '0 0 800 460',
        class: 'alg-inequality-line-svg',
      });
      host.appendChild(svg);

      // ── DOM: Panel ──

      // LHS input
      const lhsInputRow = document.createElement('div');
      lhsInputRow.className = 'alg-panel-row';
      const lhsLabel = document.createElement('label');
      lhsLabel.textContent = 'LHS';
      const lhsInput = document.createElement('input');
      lhsInput.type = 'text';
      lhsInput.className = 'alg-text-input';
      lhsInput.addEventListener('change', () => {
        controls.setState((s) => ({ ...s, lhs: lhsInput.value }));
      });
      lhsInputRow.appendChild(lhsLabel);
      lhsInputRow.appendChild(lhsInput);
      ctx.panel.appendChild(lhsInputRow);

      // Comparator dropdown
      const compRow = document.createElement('div');
      compRow.className = 'alg-panel-row alg-dropdown-row';
      const compLabel = document.createElement('label');
      compLabel.textContent = 'Comparator';
      const compSelect = document.createElement('select');
      compSelect.className = 'alg-select';
      (['<', '<=', '>', '>='] as Comparator[]).forEach((comp) => {
        const opt = document.createElement('option');
        opt.value = comp;
        opt.textContent = comp;
        compSelect.appendChild(opt);
      });
      compSelect.addEventListener('change', () => {
        controls.setState((s) => ({ ...s, comparator: compSelect.value as Comparator }));
      });
      compRow.appendChild(compLabel);
      compRow.appendChild(compSelect);
      ctx.panel.appendChild(compRow);

      // RHS input
      const rhsInputRow = document.createElement('div');
      rhsInputRow.className = 'alg-panel-row';
      const rhsLabel = document.createElement('label');
      rhsLabel.textContent = 'RHS';
      const rhsInput = document.createElement('input');
      rhsInput.type = 'text';
      rhsInput.className = 'alg-text-input';
      rhsInput.addEventListener('change', () => {
        controls.setState((s) => ({ ...s, rhs: rhsInput.value }));
      });
      rhsInputRow.appendChild(rhsLabel);
      rhsInputRow.appendChild(rhsInput);
      ctx.panel.appendChild(rhsInputRow);

      // Show sign chart toggle
      const signChartToggle = createToggleRow({
        labelKey: 'panel.show-sign-chart',
        i18n: ctx.i18n,
        checked: true,
        onChange: (v) => {
          controls.setState((s) => ({ ...s, showSignChart: v }));
        },
      });
      ctx.panel.appendChild(signChartToggle.element);

      // ── DOM: Action bar ──
      const actionBar = mountActionBar(ctx.actionBar, ctx.i18n);

      // ── Sign chart closure var ──
      let signChartHandle: ReturnType<typeof createSignChart> | null = null;

      // ── Action bar mount guard ──
      let actionsMounted = false;

      // ── RenderHandle ──
      const update = (state: State): void => {
        const { lhs, comparator, rhs, showSignChart } = state;

        // --- Sync inputs (GUIDANCE B: guard value to avoid clobbering caret) ---
        if (lhsInput.value !== lhs) lhsInput.value = lhs;
        if (compSelect.value !== comparator) compSelect.value = comparator;
        if (rhsInput.value !== rhs) rhsInput.value = rhs;

        // --- Solve + rebuild number line ---
        try {
          const lhsExpr = parse(lhs);
          const rhsExpr = parse(rhs);
          const result = solveInequality(lhsExpr, comparator, rhsExpr, TARGET_VAR);

          // Clear SVG and re-render steps + number line
          svg.replaceChildren();

          renderSteps(svg, result.steps, ctx.i18n, comparator);

          const segments = intervalsToShadedSegments(
            result.intervals,
            VIEW_RANGE,
            LINE_PIXEL_WIDTH,
          );
          const asymptoteFracs = result.criticalPoints
            .filter((cp) => cp.kind === 'asymptote')
            .map((cp) => cp.x);
          const markers = intervalsAndAsymptotesToEndpointMarkers(
            result.intervals,
            asymptoteFracs,
            VIEW_RANGE,
            LINE_PIXEL_WIDTH,
          );

          renderNumberLine(svg, segments, markers, VIEW_RANGE, LINE_PIXEL_WIDTH);

          // --- Sign chart lifecycle ---
          signChartHandle?.destroy();
          signChartHandle = null;

          if (showSignChart && result.signChart.rowLabels.length > 0) {
            const signChartGroup = createSvgEl('g', { transform: 'translate(0, 250)' });
            svg.appendChild(signChartGroup);

            const signChartOpts: SignChartOptions = {
              rowLabels: result.signChart.rowLabels,
              columnBoundaries: result.signChart.columnBoundaries,
              cells: result.signChart.cells,
              productRow: result.signChart.productRow,
              width: 800,
              height: 170,
            };

            signChartHandle = createSignChart(signChartGroup, signChartOpts);
          }
        } catch (_e) {
          // Silently fail to update
        }

        // --- Action bar: mount once ---
        if (!actionsMounted) {
          actionsMounted = true;

          actionBar.setActions([
            {
              id: 'reset',
              labelKey: 'btn.reset',
              onClick: () => {
                controls.setState((s) => {
                  const preset = INEQUALITY_PRESETS.find((p) => p.id === s.presetId)!;
                  return { ...s, lhs: preset.lhs, comparator: preset.comparator, rhs: preset.rhs };
                });
              },
            },
            {
              id: 'randomise',
              labelKey: 'btn.randomise',
              onClick: () => {
                const idx = Math.floor(ctx.prng() * INEQUALITY_PRESETS.length);
                const preset = INEQUALITY_PRESETS[idx]!;
                controls.setState((s) => ({
                  ...s,
                  presetId: preset.id,
                  lhs: preset.lhs,
                  comparator: preset.comparator,
                  rhs: preset.rhs,
                }));
              },
            },
          ]);
        }
      };

      return {
        update,
        dispose() {
          // GUIDANCE C: only destroy owned resources; harness clears containers
          signChartHandle?.destroy();
          actionBar.clear();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());
